import { Link } from 'react-router-dom';
import { DocSection, Callout } from '../../../pages/shell';
import { DocShot } from '../../DocShot';

export const toc = [
  { id: 'os', label: 'Operating system' },
  { id: 'gpu', label: 'GPU' },
  { id: 'ram-disk', label: 'RAM & disk' },
  { id: 'premiere', label: 'Adobe Premiere Pro' },
];

export default function Body() {
  return (
    <>
      <DocSection id="os" title="Operating system">
        <p>
          ClipRadar is a Windows desktop app: <strong>Windows 10 or 11, 64-bit</strong>. There's no
          macOS or Linux build and no per-platform setup branch to choose between — one installer,
          one target.
        </p>
        <DocShot
          slug="system-requirements"
          shot="hardware-fit"
          alt="The in-app hardware-fit verdict card, checking GPU, VRAM, and free disk space"
          caption="ClipRadar checks your hardware the first time you launch it and gives a plain pass/fail verdict. You can re-run the same check from Settings any time."
        />
      </DocSection>

      <DocSection id="gpu" title="GPU">
        <Callout kind="warn">
          An <strong>NVIDIA GPU with CUDA support is required.</strong> ClipRadar does not run
          without one — there is no supported "it just runs slower" mode on CPU alone.
        </Callout>
        <p>
          Every analysis pass — Visual, Speech, Faces — and Ultra Search runs on your GPU. Tester
          guidance puts the comfortable band around <strong>~6 GB of VRAM</strong>, with ClipRadar
          adapting its batch sizes down to cards with as little as <strong>~4 GB</strong> free.
        </p>
        <p>
          More VRAM doesn't unlock a different app — it means faster analysis, larger batches, and
          the full-size Ultra Search query-parser model staying warm instead of loading on demand.
          What actually matters is <strong>how much VRAM is free at the moment you search or
          analyze</strong>, not your card's rated total: if Premiere or another GPU app is already
          holding memory, ClipRadar adapts around what's left rather than failing outright. See{' '}
          <Link to="/docs/concepts/models-and-your-gpu">AI models &amp; your GPU</Link> for how that
          adaptation works and what degrades on a weaker card.
        </p>
        <p>
          No NVIDIA GPU, or CUDA isn't being detected? See{' '}
          <Link to="/docs/troubleshooting/gpu-not-detected">ClipRadar can't find my GPU</Link>.
        </p>
      </DocSection>

      <DocSection id="ram-disk" title="RAM & disk">
        <p>
          Plan for <strong>16 GB of system RAM</strong> and roughly{' '}
          <strong>20 GB of free disk space</strong> before you install:
        </p>
        <ul>
          <li>~3.2 GB for the installer payload itself</li>
          <li>~13 GB for the AI models ClipRadar downloads the first time you use each feature</li>
          <li>a growing analysis cache on top of that as you index footage</li>
        </ul>
        <p>
          See <Link to="/docs/reference/where-your-data-lives">Where ClipRadar stores things</Link>{' '}
          for where the cache lives and how to move it off a small system drive.
        </p>
      </DocSection>

      <DocSection id="premiere" title="Adobe Premiere Pro">
        <p>
          ClipRadar runs standalone — Premiere Pro is entirely optional. If you want the{' '}
          <Link to="/docs/platforms/premiere-panel">Premiere panel</Link>, the minimum version is{' '}
          <strong>Premiere Pro 22.3+</strong>. The panel is a thin window onto the same desktop app,
          so it inherits the GPU, RAM, and disk requirements above — there's nothing extra to install
          beyond the panel file itself.
        </p>
      </DocSection>
    </>
  );
}
